<script setup lang="ts">

import TheBottomSheet from '@/components/TheBottomSheet.vue'
import { computed, ref, watch } from 'vue'
import { useAnalytics } from '@/composables/analytics'
import { useEcommerce, useEcommerceItemDetailsPurchase } from '@/composables/game/ecommerce'
import { T } from '@tolgee/vue'
import CoinIcon from '@/components/icons/CoinIcon.vue'
import { formatNumberSemiTruncate } from '@/helpers/utils'
import PlusIcon from '@/components/icons/PlusIcon.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import ThePremium from '@/components/ThePremium.vue'
import { useConfetti } from '@/composables/game/confetti'
import CheckIcon from '@/components/icons/CheckIcon.vue'
import { syncPlayer } from '@/composables/game/player'
import { useFortuneWheel } from '@/composables/game/fortune-wheel'
import ShopBadge from '@/components/ShopBadge.vue'
import startPackValueImage from '@/assets/images/shop-badge-1000.png'
import { ECOMMERCE_ITEM_BENEFIT_TYPE, ECOMMERCE_ITEM_PRICE_TYPE, type EcommerceItem } from '@/types/shared/player-config'
import StarsBalancePill from '@/components/StarsBalancePill.vue'
import NumberFormatTooltip from '@/components/NumberFormatTooltip.vue'
import TonDiscountBadge from '@/components/TonDiscountBadge.vue'

const props = defineProps<{
  show: boolean
  starterPack: EcommerceItem
}>()

const emit = defineEmits<{
  (e: 'hide'): void
}>()

const { triggerEvent } = useAnalytics()
const { buyingItemId, buildItemAnalyticsMetadata } = useEcommerce()
const { showConfetti } = useConfetti()
const { updateState: updateFortuneWheelState } = useFortuneWheel()
const {
  starPrice,
  tonPrice,
  isBuyingWithStars,
  isBuyingWithTon,
  showTonPurchase,
  onPurchase,
} = useEcommerceItemDetailsPurchase(props.starterPack)

const purchaseDone = ref(false)
const coinBenefit = computed(() => props.starterPack.benefits.find(benefit => benefit.type === ECOMMERCE_ITEM_BENEFIT_TYPE.COIN))
const spinBenefit = computed(() => props.starterPack.benefits.find(benefit => benefit.type === ECOMMERCE_ITEM_BENEFIT_TYPE.SPIN))
const isPurchasing = ref(false)
const isLoading = computed(() => buyingItemId.value !== null || isPurchasing.value)
const starPurchaseLoading = computed(() => isLoading.value && isBuyingWithStars.value)
const tonPurchaseLoading = computed(() => isLoading.value && isBuyingWithTon.value)

async function doPurchase(priceType: ECOMMERCE_ITEM_PRICE_TYPE) {
  isPurchasing.value = true
  try {
    const paid = await onPurchase(priceType)
    if (paid) {
      await showConfetti()
      purchaseDone.value = true
      await syncPlayer()
      await updateFortuneWheelState()
    }
  } finally {
    isPurchasing.value = false
  }
}

function hideModal() {
  if (isPurchasing.value) {
    return
  }
  emit('hide')
}

watch(() => props.show, show => {
  if (!show) return
  const itemMetadata = buildItemAnalyticsMetadata(props.starterPack)

  triggerEvent({
    event: 'view_item',
    metadata: {
      ecommerce: {
        currency: 'USD',
        value: itemMetadata.price,
        items: buildItemAnalyticsMetadata(props.starterPack),
      },
    },
  })
})
</script>

<template>
  <TheBottomSheet
    :show="show"
    content-class="max-[375px]:pb-6"
    @hide="hideModal"
  >
    <div
      class="w-full flex flex-col items-center relative"
    >
      <StarsBalancePill
        class="absolute left-0 -top-6"
      />
      <div class="size-[114px] relative">
        <ShopBadge
          :value-image="startPackValueImage"
          outer-class="-left-1.5 -top-4 w-[70px] h-auto"
          valueable-image-class="top-3"
          value-image-class="top-[28px]"
          animate-value-image
        />
        <img
          class="w-full h-full object-cover"
          src="@/assets/images/shop-glow.png"
        >
      </div>
      <div class="mt-3 flex flex-col items-center">
        <div class="text-light-white text-[24px] leading-8 font-bold -tracking-[0.48px]">
          <span v-if="purchaseDone">
            <T
              key-name="starter_pack.title_done"
              default-value="Purchase successful!"
            />
          </span>
          <template v-else>
            <T
              key-name="starter_pack.title"
              default-value="Starter Pack"
            />
            = ${{ starPrice.usdAmount }}
          </template>
        </div>
        <div class="text-center text-white/60 text-base font-medium">
          <T
            v-if="purchaseDone"
            key-name="starter_pack.title"
            default-value="Starter Pack"
          />
          <T
            v-else
            key-name="starter_pack.subtitle"
            default-value="Boost your growth instantly!"
          />
        </div>
      </div>
      <div class="max-[375px]:mt-8 mt-12 flex flex-col items-center gap-y-3 w-max">
        <div
          v-if="coinBenefit"
          class="benefit-box benefit-box-glow"
        >
          <CheckIcon
            v-if="purchaseDone"
            class="benefit-box-check scale-in-medium"
          />
          <CoinIcon class="w-5 h-auto min-w-[20px]" />
          <NumberFormatTooltip
            :number="coinBenefit.amount"
            :formatting-type="'semi'"
          >
            <span
              class="benefit-box-value"
            >{{
              formatNumberSemiTruncate(coinBenefit.amount)
            }}</span>
          </NumberFormatTooltip>
        </div>
        <PlusIcon
          v-if="coinBenefit && spinBenefit"
          class="text-white size-4"
        />
        <div
          v-if="spinBenefit"
          class="benefit-box benefit-box-glow"
        >
          <CheckIcon
            v-if="purchaseDone"
            class="benefit-box-check scale-in-medium"
          />
          <img
            src="@/assets/images/fortune-wheel-without-border.png"
            class="size-8 min-w-[28px] object-cover"
          >
          <span class="benefit-box-value">
            <T
              key-name="fortune_wheel.info.more_spins.count_spin"
              default-value="{count, plural, one {# spin} other {# spins}}"
              :params="{count: spinBenefit.amount}"
            />
          </span>
        </div>
      </div>
      <BaseButton
        v-if="purchaseDone"
        class="mt-12 w-full"
        @click="$emit('hide')"
      >
        Close
      </BaseButton>
      <div
        v-else
        class="flex flex-col w-full max-[375px]:mt-8 mt-12"
      >
        <BaseButton
          class="w-full"
          :loading="starPurchaseLoading"
          @click="doPurchase(ECOMMERCE_ITEM_PRICE_TYPE.STAR)"
        >
          <span
            v-if="!starPurchaseLoading"
            class="flex items-center gap-x-2"
          >
            <T
              key-name="starter_pack.buy_with"
              default-value="Buy with"
            />
            <ThePremium :with-tooltip="false" />
            <span class="-ml-0.5">{{ starPrice.amount }}</span>
          </span>
          <T
            v-else
            key-name="lingo.giveaway.transaction.loading_cta"
            default-value="Processing..."
          />
        </BaseButton>
        <div class="relative w-full">
          <div class="absolute right-12 top-1 rotate-[20deg]">
            <TonDiscountBadge label="-10%" />
          </div>
          <BaseButton
            v-if="showTonPurchase"
            style-type="tertiary"
            :loading="tonPurchaseLoading"
            class="mt-3 w-full"
            @click="doPurchase(ECOMMERCE_ITEM_PRICE_TYPE.TON)"
          >
            <span
              v-if="!tonPurchaseLoading"
              class="flex items-center gap-x-2"
            >
              <T
                key-name="starter_pack.buy_with"
                default-value="Buy with"
              />
              <img
                src="@/assets/images/ton-symbol.png"
                class="size-5 min-w-[20px] object-cover"
              >
              <span>{{ tonPrice?.amount }}</span>
            </span>
            <T
              v-else
              key-name="lingo.giveaway.transaction.loading_cta"
              default-value="Processing..."
            />
          </BaseButton>
        </div>
      </div>
    </div>
  </TheBottomSheet>
</template>

<style scoped lang="scss">
@import "@/assets/styles/ecommerce";
</style>
